"use client";

import { useQuery } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import type {
  ConditionDetailsData,
  ConsentsData,
  OnboardingAppointmentData,
  EmergencyContactData,
  HealthBackgroundData,
  HomeAddressData,
  InsuranceData,
  OnboardingSectionData,
  WellBeingData,
} from "@rmh/defs";
import { OnboardingSection } from "@rmh/defs";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";
import { onboardingSectionQueryKey } from "./keys";

export { onboardingSectionQueryKey };

/** Maps each section to its payload type so the hook is typed by the requested section.*/
type OnboardingSectionResultMap = {
  [OnboardingSection.Consents]: ConsentsData;
  [OnboardingSection.Appointments]: OnboardingAppointmentData[];
  [OnboardingSection.ConditionDetails]: ConditionDetailsData;
  [OnboardingSection.EmergencyContact]: EmergencyContactData;
  [OnboardingSection.HomeAddress]: HomeAddressData;
  [OnboardingSection.Insurance]: InsuranceData;
  [OnboardingSection.HealthBackground]: HealthBackgroundData;
  [OnboardingSection.WellBeing]: WellBeingData;
};

type OnboardingSectionResult<S extends OnboardingSection> =
  S extends keyof OnboardingSectionResultMap
    ? OnboardingSectionResultMap[S]
    : never;

interface GetOnboardingSectionQueryVariables {
  section: OnboardingSection;
  patientId: string;
}

interface GetOnboardingSectionQueryData {
  getOnboardingSection?: OnboardingSectionData | null;
}

export const getOnboardingSectionQuery = gql`
  query getOnboardingSection($section: OnboardingSection!, $patientId: ID!) {
    getOnboardingSection(section: $section, patientId: $patientId) {
      consents {
        acceptedCodes
      }
      appointments {
        variant
        appointmentStatus
        appointmentDateTime
        canvasProviderId
        canvasAppointmentId
        appointmentState
        appointmentId
        ptId
        noteType
      }
      conditionDetails {
        bodyPart
        startDate
        triggerActivity
        feeling
        treatmentGoal
        hadAccident
        usingPainMedication
        onOpioidMedication
      }
      emergencyContact {
        name
        phone
        email
        relationship
      }
      homeAddress {
        address
        optionalAddress
        city
        state
        zipCode
      }
      insurance {
        payerId
        payerName
        subscriberId
        groupNumber
        relationship
        isPrimary
        subscriberFirstName
        subscriberLastName
        subscriberDob
      }
      healthBackground {
        heightFeet
        heightInches
        weightLb
        sexAtBirth
        allergies {
          fdbCode
          rxnormCode
          display
        }
        medications {
          fdbCode
          rxnormCode
          display
        }
        conditions
        conditionAnswers {
          key
          answer
        }
        habits {
          alcohol
          alcoholFrequency
          tobacco
          tobaccoFrequency
          otherSubstance
          otherSubstanceFrequency
          otherSubstanceList
          activityLevel
        }
        fallHistory {
          hadFall
          fallCount
        }
        surgicalHistory {
          hadSurgery
          surgeries {
            name
            month
            year
          }
        }
        cancer {
          status
          type
        }
      }
      wellBeing {
        phqInterest
        phqDownDepressed
        phqSleep
        phqEnergy
        phqAppetite
        phqBadAboutSelf
        phqConcentrating
        phqPsychomotor
        phqSuicidalThoughts
      }
    }
  }
`.loc?.source.body as GeneratedQuery<
  GetOnboardingSectionQueryVariables,
  GetOnboardingSectionQueryData
>;

/** Fetch an onboarding section from Canvas, typed per `section`, `null` on a cold chart. */
export const fetchOnboardingSection = async <S extends OnboardingSection>(
  patientId: string,
  section: S
): Promise<OnboardingSectionResult<S> | null> => {
  const response = await fetchGraphQLClient(getOnboardingSectionQuery, {
    section,
    patientId,
  });
  const data = response.data?.getOnboardingSection;
  switch (section) {
    case OnboardingSection.Consents:
      return (data?.consents ?? null) as OnboardingSectionResult<S> | null;
    case OnboardingSection.ConditionDetails:
      return (data?.conditionDetails ??
        null) as OnboardingSectionResult<S> | null;
    case OnboardingSection.Appointments:
      return (data?.appointments ?? null) as OnboardingSectionResult<S> | null;
    case OnboardingSection.EmergencyContact:
      return (data?.emergencyContact ??
        null) as OnboardingSectionResult<S> | null;
    case OnboardingSection.HomeAddress:
      return (data?.homeAddress ?? null) as OnboardingSectionResult<S> | null;
    case OnboardingSection.Insurance:
      return (data?.insurance ?? null) as OnboardingSectionResult<S> | null;
    case OnboardingSection.HealthBackground:
      return (data?.healthBackground ??
        null) as OnboardingSectionResult<S> | null;
    case OnboardingSection.WellBeing:
      return (data?.wellBeing ?? null) as OnboardingSectionResult<S> | null;
    default:
      return null;
  }
};

interface UseGetOnboardingSectionOptions<S extends OnboardingSection> {
  patientId: string;
  section: S;
  enabled?: boolean;
}

/**
 * Read an onboarding section from Canvas; shares its cache key with
 * `useStoreOnboardingSection` (store re-reads canonical value).
 */
export const useGetOnboardingSection = <S extends OnboardingSection>({
  patientId,
  section,
  enabled = true,
}: UseGetOnboardingSectionOptions<S>) => {
  return useQuery<OnboardingSectionResult<S> | null>({
    queryKey: onboardingSectionQueryKey(patientId, section),
    queryFn: () => fetchOnboardingSection(patientId, section),
    enabled: !!patientId && enabled,
  });
};
